import { Card, Group, Stack } from '@mantine/core';
import type {
	AISetting,
	Update as AutomationActionUpdate,
} from '@repo/api-codegen';
import { useMobxReaction } from '@repo/common/utils/useMobxReaction';
import { Switch, Text } from '@repo/foundations';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useRef, useState } from 'react';
import { BlockHeader } from '../../components/BlockHeader/BlockHeader';
import { AddEditAction } from './components/AddEditAction';
import { AiEditAction } from './components/AutomationEdit/AiEditAction';
import { EditAction } from './components/AutomationEdit/EditAction';
import { useAddEditAction } from './hooks/useAddEditAction';
import { EditBlockStore } from './store';
import { requiresEditActionValue } from './utils';

interface EditBlockControllerInternalProps {
	store: EditBlockStore;
	includeTitle?: boolean;
	onEditDelete: () => void;
	tooltip?: string;
}

const EditBlockControllerInternal = observer(
	({
		store,
		onEditDelete,
		includeTitle = true,
		tooltip,
	}: EditBlockControllerInternalProps) => {
		const { createdEditAction, handleAddEditAction, reset } = useAddEditAction({
			onAddEditAction: store.onAddEditAction,
			close: () => {},
		});

		const hasAiEditActions = Array.from(store.editActions.values()).some(
			(editActionStore) => editActionStore.editAction.isAiEditAction
		);

		const deleteTooltip = hasAiEditActions
			? 'This action has AI-generated filters tied to it. To remove it, first remove the AI output action above.'
			: (tooltip ?? 'Remove action');

		return (
			<Card withBorder radius="md" pt={0}>
				{includeTitle && (
					<BlockHeader
						title="Edit resources"
						iconName="pencil"
						onDelete={onEditDelete}
						toolTip={deleteTooltip}
						backgroundColor="fill/transparent-secondary/default"
						deleteDisabled={hasAiEditActions}
					/>
				)}
				<Card.Section p="md">
					<Group spacing="2xs">
						{Array.from(store.editActions.keys()).map((searchKey) => {
							const editActionStore = store.editActions.get(searchKey);

							if (
								!editActionStore ||
								(!editActionStore.editAction.value &&
									requiresEditActionValue(editActionStore.editAction.type))
							) {
								return null;
							}

							// Use AiEditAction for AI-generated actions
							if (editActionStore.editAction.isAiEditAction) {
								return (
									<AiEditAction
										key={searchKey}
										actionSearchKey={searchKey}
										store={store}
									/>
								);
							}

							// Use regular EditAction for user-created actions
							return (
								<EditAction
									key={searchKey}
									actionSearchKey={searchKey}
									store={store}
								/>
							);
						})}
						{/* AI bindings are auto-applied, no placeholders needed */}
						<AddEditAction
							store={store}
							createdEditAction={createdEditAction}
							handleAddEditAction={handleAddEditAction}
							reset={reset}
						/>
					</Group>
				</Card.Section>
				<Card.Section p="md" withBorder>
					<Group position="apart" align="center" noWrap>
						<Stack spacing={0}>
							<Text size="sm">Override existing properties</Text>
							<Text size="xs" variant="tertiary" color="text/secondary/default">
								When disabled, properties with pre-existing values will not be
								updated by the automation.
							</Text>
						</Stack>
						<Switch
							checked={store.overrideExistingProperties}
							onChange={(event) =>
								store.setOverrideExistingProperties(event.currentTarget.checked)
							}
						/>
					</Group>
				</Card.Section>
			</Card>
		);
	}
);

interface EditBlockControllerProps
	extends Pick<EditBlockControllerInternalProps, 'onEditDelete' | 'tooltip'> {
	onEditChange: (edit: AutomationActionUpdate[]) => void;
	initialEditSettings: AutomationActionUpdate[];
	initialShouldOverwriteMetadata: boolean;
	onShouldOverwriteMetadataChange: (shouldOverwriteMetadata: boolean) => void;
	aiSettings: AISetting[];
}

export const EditBlockController = observer(
	({
		onEditChange,
		initialEditSettings,
		initialShouldOverwriteMetadata,
		onShouldOverwriteMetadataChange,
		aiSettings,
		...rest
	}: EditBlockControllerProps) => {
		const [store] = useState(
			() =>
				new EditBlockStore(
					initialEditSettings,
					initialShouldOverwriteMetadata,
					[]
				)
		);

		// Update AI settings when they change, using ref to prevent infinite loops
		const prevAiSettingsRef = useRef<string>('');
		const currentAiSettings = JSON.stringify(aiSettings);
		if (prevAiSettingsRef.current !== currentAiSettings) {
			prevAiSettingsRef.current = currentAiSettings;
			store.updateAiSettings(aiSettings);
		}

		useMobxReaction(
			useCallback(() => store.editSettings, [store]),
			useCallback(
				(editSettings) => {
					onEditChange(toJS(editSettings));
				},
				[onEditChange]
			)
		);

		useMobxReaction(
			useCallback(() => store.overrideExistingProperties, [store]),
			useCallback(
				(shouldOverwriteMetadata) => {
					onShouldOverwriteMetadataChange(shouldOverwriteMetadata);
				},
				[onShouldOverwriteMetadataChange]
			)
		);

		return <EditBlockControllerInternal {...rest} store={store} />;
	}
);
